import { Button } from '@lensora/ui'
import { AlertTriangle, Check, Copy, ShieldAlert } from 'lucide-react'
import * as React from 'react'

import { ConnectSnippets } from './ConnectSnippets'

import type { McpApiKeyPurpose } from '@/hooks/use-mcp-keys'

// ─── Types ──────────────────────────────────────────────────────────────────

interface SecretRevealModalProps {
  keyName: string
  keyPrefix: string
  /**
   * Plaintext secret, held only in the caller's local state for the
   * lifetime of this modal — see use-mcp-keys.ts. Never written into the
   * `['mcp-keys']` query cache, and never persisted (no localStorage).
   */
  secret: string
  onDismiss: () => void
  /**
   * Defaults to 'mcp'. A `looker_studio` key has no MCP client to connect —
   * showing the Claude Desktop/Cursor/n8n tabs and an `mcpServers` JSON
   * snippet after minting a Looker key is confusing and wrong, so this
   * branches to Looker-specific setup guidance instead (matches
   * LookerInstructionsCard's copy).
   */
  purpose?: McpApiKeyPurpose
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SecretRevealModal({
  keyName,
  keyPrefix,
  secret,
  onDismiss,
  purpose = 'mcp',
}: SecretRevealModalProps) {
  const isLooker = purpose === 'looker_studio'
  const [copied, setCopied] = React.useState(false)
  const [acknowledged, setAcknowledged] = React.useState(false)

  function handleCopySecret() {
    navigator.clipboard
      .writeText(secret)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => {})
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg space-y-4 rounded-lg border border-border bg-surface p-6 shadow-lg">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-feedback-warning/10">
            <AlertTriangle className="h-4.5 w-4.5 text-feedback-warning" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-text">Save this key now</h2>
            <p className="mt-0.5 text-sm text-text-muted">
              "{keyName}" was created. This is the only time the full secret will ever be shown —
              it cannot be retrieved again after you close this dialog.
            </p>
          </div>
        </div>

        <div className="space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Secret key</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-md border border-border bg-surface-raised px-3 py-2 text-xs text-text">
              {secret}
            </code>
            <Button type="button" size="sm" variant="outline" onClick={handleCopySecret}>
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>

        {isLooker ? (
          <div className="space-y-1.5">
            <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
              Connect Looker Studio
            </p>
            <ol className="list-inside list-decimal space-y-1.5 text-sm text-text-muted">
              <li>Open the Lensora Looker Studio connector.</li>
              <li>
                Paste the secret above into its{' '}
                <span className="font-medium text-text">API Key</span> auth field.
              </li>
              <li>Add the connector as a data source to any Looker Studio report.</li>
            </ol>
            <div className="flex items-start gap-2.5 rounded-md border border-feedback-warning/30 bg-feedback-warning/5 p-3">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-feedback-warning" />
              <p className="text-xs text-text">
                The connector must send this key as an{' '}
                <code className="rounded bg-surface-raised px-1 py-0.5 text-[11px]">
                  Authorization: Bearer &lt;key&gt;
                </code>{' '}
                header — never as a URL query parameter. A key placed in a query string ends up in
                server access logs and can leak to anyone with log access.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Connect a client</p>
            <ConnectSnippets secret={secret} keyPrefix={keyPrefix} />
          </div>
        )}

        <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-3 text-sm text-text">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          I've copied this secret somewhere safe. I understand Lensora cannot show it to me again.
        </label>

        <div className="flex justify-end">
          <Button type="button" disabled={!acknowledged} onClick={onDismiss}>
            Done
          </Button>
        </div>
      </div>
    </div>
  )
}
